import { api } from './api';

const shareApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getShare: builder.query<ShareDetails, string>({
      query: (token) => `shares/${token}`,
    }),
    acceptShare: builder.mutation<ShareAccepted, AcceptShareRequest>({
      query: ({ token, account }) => ({
        url: `shares/${token}/accept`,
        method: 'POST',
        body: account ?? {},
        credentials: 'include',
      }),
    }),
  }),
});

// The state the share is in when it is redeemed. This is authoritative:
// the emailed copy was written when the share was issued and may have
// assumed the recipient had (or lacked) an account.
export type ShareState = 'pending' | 'expired' | 'spent' | 'not-found';

export type ShareDetails = {
  state: ShareState;
  label?: string;
  sharerName?: string | null;
  email?: string;
  // Whether the recipient has an account that can log in.
  usableAccount?: boolean;
  // Present only when the recipient has no usable account and their address
  // authenticates by SSO: there is no account to create here, they are sent
  // to log in and the claim proceeds from the logged-in path.
  sso?: ShareSso | null;
};

export type ShareSso = {
  organisation: string;
  redirectUrl: string;
};

export type ShareAccount = {
  firstName: string;
  lastName: string;
  jobTitle: string;
  password: string;
  acceptTerms: boolean;
};

export type AcceptShareRequest = {
  token: string;
  // Absent when the claimant is logged in — their identity claims the token.
  account?: ShareAccount;
};

// The claim was refused because the recipient has a pending organisation
// invitation they must accept before they have an account to claim with.
// Diplomat has re-sent that invitation. Nothing about the invitation, its
// organisation or the inviter travels with it.
export const SHARE_PENDING_INVITATION = 'share-pending-invitation';

export const isPendingInvitation = (error: unknown) =>
  (error as { data?: { code?: string } })?.data?.code ===
  SHARE_PENDING_INVITATION;

export type ShareAccepted = {
  object: { namespace: string; key: string };
  // Present only on the accountless path, which also logs the claimant in.
  accessToken?: string;
};

export const { useGetShareQuery, useAcceptShareMutation } = shareApi;

export { shareApi };
